import { useState } from 'react'
import { useQuery, useMutation } from 'react-query'
import { 
  Settings as SettingsIcon, 
  Save, 
  Server,
  Globe,
  Mail,
  Bell,
  Shield,
  RefreshCw,
  RotateCcw,
  Play,
  Square,
  CheckCircle
} from 'lucide-react'
import { apiService } from '../services/api'

const Settings = () => {
  const [activeTab, setActiveTab] = useState('general')
  const [serviceStatus, setServiceStatus] = useState<string | null>(null)
  
  const { data: settings, isLoading } = useQuery('settings', 
    () => apiService.getSettings()
  )

  const { data: dnsStatus, refetch: refetchDns } = useQuery('dnsStatus',
    () => apiService.getDNSServerStatus()
  )

  const restartMutation = useMutation(
    () => apiService.restartDNSServer(),
    {
      onSuccess: () => {
        setServiceStatus('DNS Server restarted successfully')
        refetchDns()
        setTimeout(() => setServiceStatus(null), 3000)
      },
      onError: () => {
        setServiceStatus('Failed to restart DNS Server')
        setTimeout(() => setServiceStatus(null), 3000)
      }
    }
  )

  const reloadMutation = useMutation(
    () => apiService.reloadDNSServer(),
    {
      onSuccess: () => {
        setServiceStatus('DNS Server reloaded successfully')
        refetchDns()
        setTimeout(() => setServiceStatus(null), 3000)
      },
      onError: () => {
        setServiceStatus('Failed to reload DNS Server')
        setTimeout(() => setServiceStatus(null), 3000)
      }
    }
  )

  const tabs = [
    { id: 'general', label: 'General', icon: SettingsIcon },
    { id: 'dns', label: 'DNS Server', icon: Server },
    { id: 'monitoring', label: 'Monitoring', icon: RefreshCw },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900">System Settings</h2>
        <p className="text-slate-500">Configure application settings</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar */}
        <div className="lg:w-64 space-y-1">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-sky-50 text-sky-700'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Icon className="h-5 w-5" />
                <span className="font-medium">{tab.label}</span>
              </button>
            )
          })}
        </div>

        {/* Content */}
        <div className="flex-1 bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner h-8 w-8 border-4 border-sky-600 border-t-transparent rounded-full" />
            </div>
          ) : (
            <>
              {activeTab === 'general' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-slate-900">General Settings</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        defaultValue="Netwall Expert Pvt Ltd"
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Session Timeout (seconds)
                      </label>
                      <input
                        type="number"
                        defaultValue="3600"
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'dns' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-slate-900">DNS Configuration</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Primary Name Server
                      </label>
                      <input
                        type="text"
                        defaultValue="ns7.perfectinternet.in"
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Secondary Name Server
                      </label>
                      <input
                        type="text"
                        defaultValue="ns8.perfectinternet.in"
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        DNS Forwarders
                      </label>
                      <input
                        type="text"
                        defaultValue="103.12.132.12, 103.12.132.13"
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                      />
                      <p className="text-sm text-slate-500 mt-1">
                        Comma-separated list of forwarder IP addresses
                      </p>
                    </div>

                    {/* DNS Server Control */}
                    <div className="pt-6 border-t border-slate-200">
                      <h4 className="text-md font-semibold text-slate-800 mb-4">DNS Server Control</h4>
                      
                      {serviceStatus && (
                        <div className={`mb-4 p-3 rounded-lg ${serviceStatus.includes('success') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {serviceStatus}
                        </div>
                      )}
                      
                      <div className="flex gap-3">
                        <button
                          onClick={() => reloadMutation.mutate()}
                          disabled={reloadMutation.isLoading}
                          className="flex items-center gap-2 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50"
                        >
                          <RefreshCw className={`h-4 w-4 ${reloadMutation.isLoading ? 'animate-spin' : ''}`} />
                          {reloadMutation.isLoading ? 'Reloading...' : 'Reload Config'}
                        </button>
                        
                        <button
                          onClick={() => restartMutation.mutate()}
                          disabled={restartMutation.isLoading}
                          className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
                        >
                          <RotateCcw className={`h-4 w-4 ${restartMutation.isLoading ? 'animate-spin' : ''}`} />
                          {restartMutation.isLoading ? 'Restarting...' : 'Restart Service'}
                        </button>
                      </div>
                      
                      <p className="text-sm text-slate-500 mt-3">
                        Reload applies configuration changes without dropping queries. Restart fully stops and starts the service.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'monitoring' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-slate-900">Monitoring Settings</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Monitoring Interval (seconds)
                      </label>
                      <input
                        type="number"
                        defaultValue="30"
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Statistics Retention (days)
                      </label>
                      <input
                        type="number"
                        defaultValue="30"
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'notifications' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-slate-900">Notification Settings</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="emailAlerts"
                        className="h-4 w-4 text-sky-600 border-slate-300 rounded"
                      />
                      <label htmlFor="emailAlerts" className="ml-2 text-sm text-slate-700">
                        Enable Email Alerts
                      </label>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Alert Email Recipients
                      </label>
                      <textarea
                        rows={3}
                        placeholder="Enter email addresses, one per line"
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'security' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-slate-900">Security Settings</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="forceHttps"
                        className="h-4 w-4 text-sky-600 border-slate-300 rounded"
                      />
                      <label htmlFor="forceHttps" className="ml-2 text-sm text-slate-700">
                        Force HTTPS
                      </label>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Minimum Password Length
                      </label>
                      <input
                        type="number"
                        defaultValue="8"
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6 pt-6 border-t border-slate-200">
                <button className="flex items-center space-x-2 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors">
                  <Save className="h-4 w-4" />
                  <span>Save Changes</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default Settings
